import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {useErrorTranslation} from "./translateErrorMessage.js";

export async function isRetentionExpiredError(error) {
    if (error?.response?.status !== 403) return false;
    try {
        const body = await error.response.clone().json();
        return body?.context?.reason === "subscription_retention_expired";
    } catch {
        return false;
    }
}

// Capture the request's render scope; parsing a response may finish after a new selection.
export async function translateReportingError(error, {isActive, onRetentionExpired, translate}) {
    const retentionExpired = await isRetentionExpiredError(error);
    if (!isActive()) return "";
    if (retentionExpired) {
        onRetentionExpired();
        return "";
    }
    return translate(error);
}

export function useReportingError(projectId, setLoading) {
    const filters = useContext(FilterContext);
    const scopeKey = JSON.stringify([projectId, filters?.since?.format("YYYY-MM-DD"), filters?.until?.format("YYYY-MM-DD"), filters?.centresAllowListed, filters?.domainsAllowListed, filters?.customerWholesale, filters?.sourcesRemoved, filters?.campaignsAllowListed, filters?.campaignSearchFilter]);
    const activeScope = useRef(null);
    if (activeScope.current?.key !== scopeKey) activeScope.current = {key: scopeKey};
    const requestScope = activeScope.current;
    const [retentionExpired, setRetentionExpired] = useState(false);
    const {et: translate} = useErrorTranslation();

    // Keep the blocked content hidden until the report's filter-change effect clears data.
    useEffect(() => setRetentionExpired(false), [scopeKey]);
    useEffect(() => {
        activeScope.current = requestScope;
        return () => { activeScope.current = null; };
    }, [requestScope]);

    const et = useCallback((error) => translateReportingError(error, {
        isActive: () => activeScope.current === requestScope,
        onRetentionExpired: () => {
            setRetentionExpired(true);
            setLoading((current) => current && typeof current === "object"
                ? Object.fromEntries(Object.keys(current).map((key) => [key, false]))
                : false);
        },
        translate,
    }), [requestScope, translate, setLoading]);

    return {et, retentionExpired};
}
